import dayjs from "dayjs";
import * as XLSX from "xlsx";

import {
  fetchClientAddresses,
  fetchClientFiscalAddress,
} from "~/services/addressService";
import { Address, Client } from "~/types/client";
import { formatCurrency, formatRut } from "~/utils/helpers";

/**
 * The mapped `Client` returned by `useClientDetail` carries a few extra runtime
 * fields (set via `as any`) that aren't on the shared `Client` interface. Surface
 * them here so the ficha can read them without widening the shared type.
 */
type ClientWithContactExtras = Client & {
  collection_contact_person?: string;
  collection_contact_email?: string;
  collection_contact_phone?: string;
  sales_channel_name?: string;
  auxiliary_type_name?: string;
};

interface ExportClientFichaOptions {
  client: Client;
  /** Resolved sales-condition name (the client only stores the id). */
  salesConditionName?: string;
}

const DASH = "—";

/** One-line "Calle N°, complemento, comuna, ciudad, región" address string. */
const formatAddressLine = (address: Address | null | undefined): string => {
  if (!address) return "";
  return [
    [address.street, address.number].filter(Boolean).join(" ").trim(),
    address.complement,
    address.comuna,
    address.city,
    address.state,
  ]
    .filter((part) => part && String(part).trim())
    .join(", ");
};

/**
 * Exports a single-sheet client "ficha" (.xlsx) with all the client data a
 * seller needs: razón social, RUT, giro, estado, contacto comercial, contacto
 * de cobranza, información comercial, dirección fiscal y direcciones de
 * despacho. Addresses are fetched on demand. Returns the generated filename.
 */
export async function exportClientFichaToExcel({
  client,
  salesConditionName,
}: ExportClientFichaOptions): Promise<string> {
  const c = client as ClientWithContactExtras;
  const clientId = String(client.id);

  const [addresses, fiscalAddress] = await Promise.all([
    fetchClientAddresses(clientId),
    fetchClientFiscalAddress(clientId),
  ]);

  const isActive = client.is_active ?? client.isActive;
  const estadoFlags = [
    isActive ? "Activo" : "Inactivo",
    client.risk_checked ? "Verificado" : "No verificado",
    client.siniestro ? "Siniestro" : null,
    client.cobranza_judicial ? "Cobranza judicial" : null,
  ].filter(Boolean);

  // Vertical label/value layout (array-of-arrays). Section titles sit on their
  // own row; the despacho table at the end uses the full 6-column width.
  const rows: (string | number)[][] = [];
  const section = (title: string) => {
    rows.push([]);
    rows.push([title]);
  };
  const field = (label: string, value: unknown) => {
    const str =
      value === null || value === undefined || value === "" ? DASH : String(value);
    rows.push([label, str]);
  };

  rows.push(["FICHA DE CLIENTE"]);
  rows.push(["Generada", dayjs().format("DD/MM/YYYY HH:mm")]);

  section("DATOS GENERALES");
  field("Razón social", client.name || client.company);
  field("RUT", formatRut(client.taxId || client.tax_id));
  field("Giro", client.activity);
  field("Estado", estadoFlags.join(" · "));

  section("CONTACTO COMERCIAL");
  field("Nombre de contacto", c.contact_person);
  field("Email", client.email);
  field("Teléfono", client.phone);

  section("CONTACTO DE COBRANZA");
  field("Nombre de contacto", c.collection_contact_person);
  field("Email", c.collection_contact_email);
  field("Teléfono", c.collection_contact_phone);

  section("INFORMACIÓN COMERCIAL");
  field("Vendedor asignado", client.sellerName);
  field("Canal de venta", c.sales_channel_name);
  field("Tipo auxiliar", c.auxiliary_type_name);
  field("Zona", client.zone);
  field("Condición de venta", salesConditionName);
  field(
    "Monto asegurado (línea de crédito)",
    client.insuredAmount ? `$${formatCurrency(client.insuredAmount)}` : DASH
  );
  field(
    "Días promedio de pago",
    client.trailingPaymentDays ? `${client.trailingPaymentDays} días` : DASH
  );

  section("DIRECCIÓN FISCAL");
  field("Dirección", formatAddressLine(fiscalAddress) || DASH);

  section("DIRECCIONES DE DESPACHO");
  if (addresses.length === 0) {
    rows.push(["Sin direcciones de despacho registradas"]);
  } else {
    rows.push(["#", "Dirección", "Comuna", "Ciudad", "Región", "Principal"]);
    addresses.forEach((address, index) => {
      const streetLine =
        [address.street, address.number].filter(Boolean).join(" ").trim() +
        (address.complement ? `, ${address.complement}` : "");
      rows.push([
        index + 1,
        streetLine || DASH,
        address.comuna || DASH,
        address.city || DASH,
        address.state || DASH,
        address.isDefault ? "Sí" : "No",
      ]);
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [
    { wch: 32 }, // labels / # column
    { wch: 48 }, // values / dirección
    { wch: 20 }, // comuna
    { wch: 20 }, // ciudad
    { wch: 20 }, // región
    { wch: 12 }, // principal
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Ficha Cliente");

  const safeName =
    (client.name || client.company || "cliente")
      .replace(/[^\p{L}\p{N}]+/gu, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase() || "cliente";
  const filename = `ficha_cliente_${safeName}_${dayjs().format("YYYY-MM-DD")}.xlsx`;
  XLSX.writeFile(wb, filename);

  return filename;
}
